import React from "react";
import { MessageCircle } from "lucide-react";
import { useOrg } from "../contexts/OrganizationContext";
import { useSearch } from "../contexts/SearchContext";
import { useBodyClass } from "../hooks/useBodyClass";
import TransparentSearch from "../components/Search/TransparentSearch";
import ChildCareCallout from "../components/ct/ChildCareCallout";
import CTSocialLinks from "../components/ct/CTSocialLinks";
import { getCategoriesForOrg } from "./home/guidedSearchData";
import Footer from "../components/Footer";
import Government from "../icons/Government";
import HeartHand from "../icons/HeartHand";

function GuidedSearchWithIcons({ categories, className }) {
  return (
    <div className={`list home-guided-search ${className || ""}`}>
      <div className="home-guided-search-container">
        <p className="direct">All Categories</p>
        <ul className="guided-search">
          {categories.map((cat, i) => (
            <li className="guided-item" key={i}>
              <a className="list-link" href={cat.href}>
                <figure>
                  <i className={`guided-icon ${cat.icon || ""}`} />
                  <figcaption className="guided-details">
                    <h6 className={`guided-title ${className || ""}`}>{cat.title}</h6>
                  </figcaption>
                </figure>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function GuidedSearchTAAM({ categories }) {
  return (
    <div className="home-guided-search">
      <div className="home-guided-search-container">
        <div>
          <h3 className="subheader">ALL CATEGORIES</h3>
        </div>
        <div className="guided-search">
          {categories.map((cat, i) => (
            <div className="guided-item" key={i}>
              <a className="list-link" href={cat.href}>
                <h3 className="guided-title">{cat.title}</h3>
              </a>
            </div>
          ))}
        </div>
        <div id="footer">
          <div id="logo">
            <a href="https://www.211colorado.org/">
              Resource data provided in partnership with 211 Colorado
            </a>
          </div>
          <div id="social">
            <ul className="menu-social">
              <li className="nav-item">
                <a className="nav-link" href="https://www.facebook.com/ToughasaMotherCO">
                  <i className="ico ico-facebook" />
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="https://twitter.com/tough_asamother">
                  <i className="ico ico-twitter" />
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="https://www.youtube.com/channel/UC3-X737UG4P_NscCHstKahw">
                  <i className="ico ico-youtube" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function CTSectionContainer({ children, className = "" }) {
  return <div className={`ct-section-container ${className}`.trim()}>{children}</div>;
}

function CTSections({ organization }) {
  return (
    <>
      <CTSectionContainer>
        <section className="double-screener">
          <div className="screeners">
            <div className="screener">
              <div>
                <Government />
                <h2>Government programs</h2>
                <p>
                  The 211 Eligibility Navigator helps identify state and federal programs you or your family may qualify for based on your needs and eligibility.
                </p>
              </div>
              <a href="https://www.211navigator.org/" className="btn-tertiary">
                Complete the Screener
              </a>
            </div>
            <div className="screener">
              <div>
                <HeartHand />
                <h2>Programs for improving your health</h2>
                <p>
                  The 211 CT HealthyLives Navigator helps identify health-related needs and connects you with community programs and support services that can help.
                </p>
              </div>
              <a href="https://www.211ct.org/assessments/1" className="btn-tertiary">
                Complete the Screener
              </a>
            </div>
            <a className="all-screeners" href="https://www.211ct.org/surveys">
              Or view all screeners &rarr;
            </a>
          </div>
        </section>
      </CTSectionContainer>

      <CTSectionContainer>
        <section className="specialized-programs">
          <div>
            <h2>Specialized Programs</h2>
            <p>
              We enjoy helping every way we can. In addition to referring callers to
              a wide range of health and human service resources, 211 offers
              specialized programs to address specific needs and situations.
            </p>
          </div>
          <ul>
            <li>
              <a href="https://cdi.211ct.org">
                <img src="images/child-development.png" alt="child and woman playing" />
                <h3>211 Child Development</h3>
                <p>
                  Support and resources for children's development and growth
                  starting from pregnancy
                </p>
                <span>Learn more</span>
              </a>
            </li>
            <li>
              <a href="http://www.211childcare.org">
                <img src="images/child-care.png" alt="toddlers playing with blocks" />
                <h3>211 Child Care</h3>
                <p>Find child care options</p>
                <span>Learn more</span>
              </a>
            </li>
            <li>
              <a href="http://www.cthcvp.org">
                <img src="images/housing-voucher.png" alt="apartment buildings" />
                <h3>Housing Voucher</h3>
                <p>
                  View open Section 8 waitlists; register to receive notifications
                  for future waitlist openings
                </p>
                <span>Learn more</span>
              </a>
            </li>
          </ul>
        </section>
      </CTSectionContainer>

      <CTSectionContainer>
        <section className="home-about">
          <div>
            <span>
              211 is the confidential, free, 24-hour a day, and multilingual way to
              locate hundreds of human services in your area.
            </span>
            <a className="btn-tertiary" href="https://uwc.211ct.org/about-211/">
              Learn more about 211
            </a>
          </div>
          <div className="about-us-links">
            <a href="tel:211">Inside of Connecticut? Dial 2-1-1</a>
            <a href="tel:18002031234">Outside of Connecticut? Dial 1-800-203-1234</a>
            <a href="tel:711">Hearing Impaired? Dial 7-1-1</a>
            <ChatButton organization={organization} />
            <CTSocialLinks />
          </div>
        </section>
      </CTSectionContainer>

      <CTSectionContainer>
        <div className="calendar-region">
          {typeof window.HomeCalendar !== "undefined" && <window.HomeCalendar />}
        </div>
      </CTSectionContainer>

      <CTSectionContainer className="ct-section-container-community">
        <div className="home-community" role="img" aria-label="Collage of faces" />
      </CTSectionContainer>
    </>
  );
}

function ARSections({ organization }) {
  return (
    <>
      <div className="wrap home-about-container">
        <div className="home-about">
          <div className="intro">
            <div className="intro-text">
              <h3 className="headliner">
                {organization.name} connects callers, at no cost, to critical
                health and human services in their community.
              </h3>
            </div>
            <a
              className="btn btn-primary medium"
              href={organization.navbar?.About?.items?.["Arkansas 211"] || "#"}
            >
              Learn More About {organization.name}
            </a>
            <p className="direct">
              If you're unable to find a service, please dial 211 for assistance.
            </p>
          </div>
        </div>
      </div>

      <div className="home-community">
        <div className="wrap">
          <div className="intro-modal">
            <div className="intro">
              <h3 className="headliner">Helping Arkansans live better.</h3>
              <p className="intro-paragraph">
                Ever wonder how to find health and community services when you or
                someone you know needs help? When a human story touches your heart
                and you want to reach out and give to those in need, where do you
                turn? Whether you want to give or get help, {organization.name} is
                the confidential, free, 24-hour a day and multilingual way to
                locate hundreds of human services in your area.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="home-partners">
        <div className="wrap">
          <div className="intro">
            <h3 className="headliner">Our Partners</h3>
            <p className="intro-paragraph">
              {organization.name} partners with corporations, governments and
              nonprofits to address complex challenges and build stronger
              communities throughout the state.
            </p>
          </div>
          <div className="partners-logos">
            <a className="partner-link" href="https://unitedwaynwa.org/">
              <img src="/images/uwnwar-logo.jpg" alt="United Way of Northwest Arkansas" style={{ height: 110 }} />
            </a>
            <a className="partner-link" href="https://blueandyoufoundationarkansas.org/">
              <img src="/images/blue-and-you-logo.png" alt="Blue & You Foundation" style={{ height: 100 }} />
            </a>
            <a className="partner-link" href="https://www.harknwa.com">
              <img src="/images/hark-logo.png" alt="Hark" style={{ height: 110 }} />
            </a>
          </div>
          <div className="partners-logos" style={{ marginTop: 0 }}>
            <a className="partner-link" href="https://walmart.org">
              <img src="/images/ar/walmart-org-logo.png" alt="Walmart.org" style={{ height: 50 }} />
            </a>
            <a className="partner-link" href="https://www.211ct.org/">
              <img src="/images/share_email/uw-logo-blue.png" alt="211 Connecticut" style={{ height: 40 }} />
            </a>
            <a className="partner-link" href="https://www.adhe.edu/engage-ar-americorps/">
              <img src="/images/engage-ar-americorps-logo.png" alt="Americorps Engage Arkansas" style={{ height: 60 }} />
            </a>
          </div>
          <div className="partners-logos" style={{ marginTop: 0 }}>
            <a className="partner-link" href="https://cjrw.com/">
              <img src="/images/cjrw-logo.png" alt="CJRW" style={{ height: 50 }} />
            </a>
            <a className="partner-link" href="https://onearkansasnil.com/">
              <img src="/images/ar/one-arkansas-logo.png" alt="One Arkansas" style={{ height: 50 }} />
            </a>
          </div>
          <a
            className="btn btn-primary medium"
            href={`mailto:${organization.emailAddresses?.contact}?subject=Request to Partner with UWNWA`}
          >
            Partner With Us
          </a>
        </div>
      </div>

      <aside className="servicestrip">
        <h6 className="direct">How to get involved</h6>
        <div className="wrap servicestrip-blocks">
          <div className="column">
            <div className="block servicestrip-block">
              <div className="involved-icon donate" />
              <h5 className="sub-headliner servicestrip-block-title">Support Arkansas 211</h5>
              <p className="servicestrip-block-copy">Help us support the most vulnerable in our community.</p>
              <a className="servicestrip-block-copy primary-link" href="https://211arkansas.org/support">Donate Now</a>
            </div>
          </div>
          <div className="column">
            <div className="block servicestrip-block">
              <div className="involved-icon partner" />
              <h5 className="sub-headliner servicestrip-block-title">Partner With Us</h5>
              <p className="servicestrip-block-copy">Join a meaningful partnership to support our work.</p>
              <a className="servicestrip-block-copy primary-link" href={`mailto:${organization.emailAddresses?.contact}?subject=Request to Partner with UWNWA`}>Learn More</a>
            </div>
          </div>
          <div className="column">
            <div className="block servicestrip-block">
              <div className="involved-icon list-agency" />
              <h5 className="sub-headliner servicestrip-block-title">List Your Agency</h5>
              <p className="servicestrip-block-copy">Make sure you can be found through 211.</p>
              <a className="servicestrip-block-copy primary-link" href="https://211arkansas.org/update">Apply Now</a>
            </div>
          </div>
          <div className="column">
            <div className="block servicestrip-block">
              <div className="involved-icon update-agency" />
              <h5 className="sub-headliner servicestrip-block-title">Update Your Agency</h5>
              <p className="servicestrip-block-copy">Keep your listing current.</p>
              <a className="servicestrip-block-copy primary-link" href="https://211arkansas.org/update">Update Information</a>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function ChatButton({ organization }) {
  const chatImplemented = organization.flags?.enableChat;
  const chatLink = organization.links?.chat;
  const newChat = organization.flags?.newChat;

  function handleClick() {
    if (chatImplemented && chatLink) {
      if (newChat) {
        window.launchChatByOpenEntryPoint();
      } else {
        window.open(
          chatLink,
          "211 Chat",
          "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,top=200,left=500,width=500,height=650"
        );
      }
    }
  }

  return (
    <button className="about-us-chat" onClick={handleClick}>
      <MessageCircle aria-hidden="true" size={16} strokeWidth={2} style={{ marginRight: "4px" }} />
      Chat with us
    </button>
  );
}

function HomePage() {
  const { organization, identifier } = useOrg();
  const { clearAll } = useSearch();
  const categories = getCategoriesForOrg(identifier);

  useBodyClass("page-root");

  // Clear search state on mount (mirrors root() controller behavior)
  React.useEffect(() => {
    clearAll();
    sessionStorage.removeItem("lastSearchUrl");
  }, []);

  const heroClass = identifier === "CO"
    ? "home-hero home-hero-co"
    : identifier === "TAAM"
    ? "home-hero home-hero-taam"
    : "home-hero";

  return (
    <div className="home-layout">
      <section className={heroClass}>
        <div className="transparent-wrap">
          <div className="hero-content">
            <div className="search-region">
              <TransparentSearch />
              {identifier === "CT" && organization.hero?.showChildCareResourcesLink && (
                <ChildCareCallout />
              )}
            </div>
            {identifier === "CT" && (
              <div className="hero-image-wrapper">
                <img
                  className="hero-image"
                  src="/images/Hero-BG-CT.png"
                  alt="Mother and child sharing a joyful moment"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Guided Search Categories */}
      {identifier === "CO" && <GuidedSearchWithIcons categories={categories} className="co" />}
      {identifier === "AR" && <GuidedSearchWithIcons categories={categories} />}
      {identifier === "TAAM" && <GuidedSearchTAAM categories={categories} />}

      {/* Org-specific sections */}
      {identifier === "CT" && <CTSections organization={organization} />}
      {identifier === "AR" && <ARSections organization={organization} />}

      {/* Footer */}
      <div className="footer-region">
        <Footer uw211Identifier={identifier} />
      </div>
    </div>
  );
}

export default HomePage;
